import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useTenantBranding } from '../../contexts/ThemeContext';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';

interface LoginFormValues {
  email: string;
  password: string;
}

interface LocationState {
  from?: {
    pathname: string;
  };
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { branding } = useTenantBranding();
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>();

  const from =
    (location.state as LocationState)?.from?.pathname ?? '/dashboard';

  async function onSubmit(values: LoginFormValues) {
    setAuthError(null);
    const { error } = await supabase.auth.signInWithPassword({
      email: values.email,
      password: values.password,
    });

    if (error) {
      setAuthError('Email ou mot de passe incorrect. Veuillez réessayer.');
      return;
    }

    navigate(from, { replace: true });
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-cime-bg p-4">
      <div className="w-full max-w-[420px]">
        {/* Logo / Firm name */}
        <div className="text-center mb-8">
          {branding.logo_url ? (
            <img
              src={branding.logo_url}
              alt={branding.firm_name}
              className="h-12 mx-auto mb-4 object-contain"
            />
          ) : (
            <div className="mb-4">
              <span
                className="text-2xl font-bold text-cime-primary"
                style={{ fontFamily: 'var(--cime-font-heading)' }}
              >
                {branding.firm_name}
              </span>
            </div>
          )}
          <h1
            className="text-3xl font-bold text-cime-primary mb-1"
            style={{ fontFamily: 'var(--cime-font-heading)' }}
          >
            Accès cabinet
          </h1>
          <p className="text-sm text-neutral-500">
            Connectez-vous à votre espace
          </p>
        </div>

        {/* Card */}
        <div className="bg-cime-surface rounded-cime border border-neutral-200 shadow-sm p-8">
          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
            <Input
              label="Adresse email"
              type="email"
              autoComplete="email"
              placeholder="vous@cime-avocats.com"
              error={errors.email?.message}
              {...register('email', {
                required: "L'adresse email est requise",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Adresse email invalide',
                },
              })}
            />

            {/* Password with toggle */}
            <div className="flex flex-col gap-1">
              <label
                htmlFor="password"
                className="text-sm font-medium text-neutral-700"
              >
                Mot de passe
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="••••••••••••"
                  className="w-full h-10 px-3 pr-10 text-sm rounded-cime border border-neutral-300 bg-white placeholder:text-neutral-400 text-neutral-900 focus:outline-none focus:ring-2 focus:ring-cime-primary focus:border-transparent transition-colors hover:border-neutral-400"
                  aria-invalid={!!errors.password}
                  {...register('password', {
                    required: 'Le mot de passe est requis',
                  })}
                />
                <button
                  type="button"
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors"
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" aria-hidden />
                  ) : (
                    <Eye className="w-4 h-4" aria-hidden />
                  )}
                </button>
              </div>
              {errors.password ? (
                <p className="text-xs text-red-600" role="alert">
                  {errors.password.message}
                </p>
              ) : null}
            </div>

            {/* Auth error */}
            {authError ? (
              <div
                role="alert"
                className="rounded-cime bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700"
              >
                {authError}
              </div>
            ) : null}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              isLoading={isSubmitting}
              className="w-full mt-2"
            >
              Se connecter
            </Button>
          </form>

          <div className="mt-4 text-center">
            <Link
              to="/forgot-password"
              className="text-sm text-cime-primary hover:underline"
            >
              Mot de passe oublié ?
            </Link>
          </div>
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-neutral-400">
          © {new Date().getFullYear()} {branding.firm_name}
        </p>
      </div>
    </div>
  );
}
